import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';

import { legShape, passengersShape } from 'constants/propTypes';
import FormattedCurrency from 'components/ui/FormattedCurrency';

import { sum } from 'utils/calculator';
import { totalExtras } from 'utils/payment';
import DigestHeader from './DigestHeader';
import JourneySegmentContainer from './JourneySegmentContainer';
import { hasAllLegSelectedBasedOnJourneyType } from './JourneyDigest.utils';

import {
  DirectionTotal,
  ExposedContinueWrapper,
  JourneyInfoContainer,
  OfflineFee,
  StyledJourneySummary,
  SubmitContainer,
  ErrorContainer,
  SubmitButtonsContainer
} from './JourneyDigest.styles';

const JourneyDigest = ({
  autoExpand,
  outbound,
  outboundTotalPrice,
  outboundTotalPoints,
  inbound,
  inboundTotalPrice,
  inboundTotalPoints,
  totalPrice,
  passengers,
  currency,
  isReturnJourney,
  offlineFee,
  confirmButton,
  confirmProvisionalButton,
  confirmError,
  originUIC,
  destinationUIC,
  outboundExtras,
  inboundExtras,
  isBookingMice,
  infantCount,
  isLoyaltyBooking = false
}) => {
  const [isOpen, setOpen] = useState(false);
  const toggleOpen = () => setOpen(!isOpen);

  useEffect(() => {
    if (autoExpand && hasAllLegSelectedBasedOnJourneyType({ isReturnJourney, outbound, inbound })) {
      setOpen(true);
    }
  }, [isReturnJourney, outbound, inbound, autoExpand]);

  const hasConfirmButton = Boolean(confirmButton);
  const hasConfirmProvisionalButton = Boolean(confirmProvisionalButton);
  const hasFees = offlineFee && offlineFee.price > 0;
  const submitContainerJustifyClass = hasConfirmButton ? 'space-between' : 'flex-end';

  const outboundJourneys = (outbound.length > 0 && outbound) || [undefined];
  const inboundJourneys = (inbound.length > 0 && inbound) || [undefined];
  const totalPoints = sum([outboundTotalPoints, inboundTotalPoints]);
  const isAllJourneysSelected = hasAllLegSelectedBasedOnJourneyType({ isReturnJourney, outbound, inbound });

  return (
    <>
      {hasConfirmButton && !isOpen && (
        <>
          <ExposedContinueWrapper span={14} start={2}>
            {confirmButton}
          </ExposedContinueWrapper>
          {confirmError ? (
            <ErrorContainer span={14} start={2}>
              {confirmError}
            </ErrorContainer>
          ) : null}
        </>
      )}
      <StyledJourneySummary data-cy="journey-digest">
        <DigestHeader
          onClick={toggleOpen}
          open={isOpen}
          isReturnJourney={isReturnJourney}
          currency={currency}
          total={totalPrice}
          totalPoints={totalPoints}
          outbound={outbound[0]}
          inbound={inbound[0]}
          isAllJourneysSelected={isAllJourneysSelected}
        />

        <JourneyInfoContainer open={isOpen} isReturnJourney={isReturnJourney} data-testid="journey-info-container">
          <div>
            {outboundJourneys.map((journey, index) => (
              <JourneySegmentContainer
                key={`${index}_${journey?.legs?.serviceIdentifier}`}
                currency={currency}
                originUIC={originUIC}
                destinationUIC={destinationUIC}
                journey={journey}
                passengers={passengers}
                direction="outbound"
                isBookingMice={isBookingMice}
                infantCount={infantCount}
                isLoyaltyBooking={isLoyaltyBooking}
              />
            ))}

            {outboundExtras?.length > 0 && (
              <DirectionTotal>
                <span data-cy="outbound-segment-extras-total" data-testid="outbound-extras">
                  <FormattedMessage id="extrasOnlyBookings.extras" />
                  :&nbsp;
                  <FormattedCurrency currency={currency} amount={totalExtras(outboundExtras)} />
                </span>
              </DirectionTotal>
            )}

            <DirectionTotal>
              <span data-cy="outbound-segment-total">
                <FormattedMessage id="journeySummary.totalOutbound" />
                :&nbsp;
                <FormattedCurrency currency={currency} amount={outboundTotalPrice} />
                {!!outboundTotalPoints && (
                  <div data-cy="outbound-points-total">
                    <> + </>
                    <FormattedMessage id="searchResults.pts" values={{ points: outboundTotalPoints }} />
                  </div>
                )}
              </span>
            </DirectionTotal>
          </div>
          {isReturnJourney && (
            <div>
              {inboundJourneys.map((journey, index) => (
                <JourneySegmentContainer
                  key={`${index}_${journey?.legs?.serviceIdentifier}`}
                  currency={currency}
                  originUIC={originUIC}
                  destinationUIC={destinationUIC}
                  journey={journey}
                  passengers={passengers}
                  direction="inbound"
                  isBookingMice={isBookingMice}
                  infantCount={infantCount}
                  isLoyaltyBooking={isLoyaltyBooking}
                />
              ))}

              {inboundExtras?.length > 0 && (
                <DirectionTotal>
                  <span data-cy="inbound-segment-extras-total" data-testid="inbound-extras">
                    <FormattedMessage id="extrasOnlyBookings.extras" />
                    :&nbsp;
                    <FormattedCurrency currency={currency} amount={totalExtras(inboundExtras)} />
                  </span>
                </DirectionTotal>
              )}

              <DirectionTotal>
                <span data-cy="inbound-segment-total">
                  <FormattedMessage id="journeySummary.totalInbound" />
                  :&nbsp;
                  <FormattedCurrency currency={currency} amount={inboundTotalPrice} />
                  {!!inboundTotalPoints && (
                    <div data-cy="inbound-points-total">
                      <> + </>
                      <FormattedMessage id="searchResults.pts" values={{ points: inboundTotalPoints }} />
                    </div>
                  )}
                </span>
              </DirectionTotal>
            </div>
          )}
          <SubmitContainer isReturnJourney={isReturnJourney} justifyContent={submitContainerJustifyClass}>
            <SubmitButtonsContainer>
              {hasConfirmButton && isOpen && confirmButton}
              {hasConfirmProvisionalButton && isOpen && confirmProvisionalButton}
            </SubmitButtonsContainer>

            {hasFees && (
              <OfflineFee data-cy="offline-fee" data-testid="offline-fee">
                <FormattedMessage id="journeySummary.offlineFee" /> <FormattedCurrency currency={currency} amount={offlineFee.price} />
              </OfflineFee>
            )}
          </SubmitContainer>
          {confirmError ? <SubmitContainer>{confirmError}</SubmitContainer> : null}
        </JourneyInfoContainer>
      </StyledJourneySummary>
    </>
  );
};

JourneyDigest.propTypes = {
  outbound: PropTypes.arrayOf(legShape),
  inbound: PropTypes.arrayOf(legShape),
  outboundTotalPrice: PropTypes.number,
  inboundTotalPrice: PropTypes.number,
  outboundTotalPoints: PropTypes.number,
  inboundTotalPoints: PropTypes.number,
  totalPrice: PropTypes.number,
  passengers: passengersShape.isRequired,
  currency: PropTypes.string,
  isReturnJourney: PropTypes.bool,
  offlineFee: PropTypes.shape({
    code: PropTypes.string,
    name: PropTypes.string,
    passengerId: PropTypes.string,
    price: PropTypes.number,
    tariffCode: PropTypes.string
  }),
  autoExpand: PropTypes.bool,
  confirmButton: PropTypes.node,
  confirmProvisionalButton: PropTypes.node,
  confirmError: PropTypes.node,
  originUIC: PropTypes.string,
  destinationUIC: PropTypes.string,
  outboundExtras: PropTypes.arrayOf(legShape),
  inboundExtras: PropTypes.arrayOf(legShape),
  isBookingMice: PropTypes.bool,
  infantCount: PropTypes.number,
  isLoyaltyBooking: PropTypes.bool
};

export default JourneyDigest;
